import React from 'react';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import { useTranslation } from 'react-i18next';
import {
  KeyStudentEngagementAssessmentAcceleratorResult,
  Student
} from 'types/schema-generated-types';
import AllModuleAssessmentAcceleratorLayout from '../../../AccessmentAcceleratorComponents/AllModuleAssessmentAcceleratorLayout';
import AssessmentStatusDetails from '../../../AccessmentAcceleratorComponents/AssessmentStatusDetails';
import ModuleTabSelector from '../../../AccessmentAcceleratorComponents/ModuleTabSelector';
import {
  buildSingleStudentModuleData,
  buildStudentAssessmentHeaderList,
  isAllSelected
} from '../../../keyboarding-report-util';
import AllModuleAssessmentCharts from '../../../AccessmentAcceleratorComponents/AllModuleAssessmentCharts';
import SingleStudentChart from './components/SingleStudentChart';
import MaybeShowErrorMessageWithLoading from 'components/ReportsModule/components/common/MaybeShowErrorMessageWithLoading';
import SingleModuleAttemptHeader from '../StudentAccessmentAcceleratorTableCard/components/SingleModuleAttemptHeader';
import { Button, makeStyles } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import useStore from 'store';
import ViewAllAttemptsModal from '../../../AccessmentAcceleratorComponents/ViewAllAttemptsModal';
import useBooleanSwitch from '../../../hooks/use-boolean-switch';
import useModuleTabSelector from '../../../hooks/use-module-tab-selector';

type Props = {
  loading?: boolean;
  reportData?: KeyStudentEngagementAssessmentAcceleratorResult & { student: Student };
};

const StudentAssessmentAcceleratorChart: React.FC<Props> = ({ loading, reportData }) => {
  const { t } = useTranslation(['Reports', 'ReportsHelpCards', 'ReportsHelpCommon']);
  const [, { openQuarterModal }] = useStore();

  const [{ selectedModule, showAll }, { setSelectedModule }] = useModuleTabSelector();
  const classes = useStyles();
  const {
    data,
    selectedModuleData,
    attempt1Completed,
    attempt2Completed
  } = buildSingleStudentModuleData(reportData!, selectedModule!);
  const studentData: Student = (data as any)?.[0]?.student!;

  const headerListData: any = isAllSelected(selectedModule) ? reportData : selectedModuleData;
  const shouldRender =
    headerListData?.activityTypes?.length > 0 || headerListData?.attempts?.length > 0;

  const TopComponent = useBooleanSwitch(
    <AllModuleAssessmentCharts reportData={reportData!} selectedModule={selectedModule} />,
    <SingleStudentChart
      data={data}
      attempt1Completed={attempt1Completed}
      attempt2Completed={attempt2Completed}
    />
  );

  const OuterHeader = useBooleanSwitch(
    <SingleModuleAttemptHeader
      title={selectedModule}
      showDivider={false}
      firstAttempt={attempt1Completed!}
      secondAttempt={attempt2Completed!}
      moduleData={selectedModuleData!}
      showAttempt={false}
      isAllModule={isAllSelected(selectedModule)}
    />,
    <AssessmentStatusDetails
      title={selectedModule}
      headerList={
        buildStudentAssessmentHeaderList(headerListData, t, isAllSelected(selectedModule)) as any
      }
    />
  );

  const handleViewAllAttempts = () => {
    openQuarterModal({
      body: (
        <ViewAllAttemptsModal
          studentData={studentData}
          module={selectedModule}
          moduleData={selectedModuleData!}
        />
      ),
      options: {
        paperProps: { className: classes.modalContainer },
        containerClass: classes.containerClass
      }
    });
  };

  const maybeViewAllAttempts = !isAllSelected(selectedModule) && {
    titleEndAdornment: (
      <Button
        disabled={data?.length === 0}
        size="small"
        variant="text"
        style={{ fontSize: '14px', fontWeight: 450 }}
        onClick={handleViewAllAttempts}
        startIcon={<ArrowForwardIcon />}
      >
        {t('viewAll', { ns: 'ReportsHelpCommon' })}
      </Button>
    )
  };

  return (
    <ReportCard
      title={t('Assessment Accelerator')}
      helpText={t('assessmentAccelerator', { ns: 'ReportsHelpCards' })}
      loading={loading!}
      reportData={reportData?.activityTypes}
      endAdornment={
        <ModuleTabSelector onChange={setSelectedModule} selectedModule={selectedModule} />
      }
      {...maybeViewAllAttempts}
    >
      <MaybeShowErrorMessageWithLoading shouldRender={shouldRender} loading={loading!}>
        <AllModuleAssessmentAcceleratorLayout
          title={selectedModule}
          OutsideHeader={OuterHeader(!showAll)}
          TopComponent={TopComponent(showAll)}
          headerList={buildStudentAssessmentHeaderList(
            headerListData,
            t,
            isAllSelected(selectedModule)
          )}
        />
      </MaybeShowErrorMessageWithLoading>
    </ReportCard>
  );
};

const useStyles = makeStyles(() => ({
  modalContainer: {
    width: 400
  },
  containerClass: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column'
  }
}));

export default StudentAssessmentAcceleratorChart;
